import { useState } from 'react';
import { Button } from '@/shared/components/ui/button';
import { Checkbox } from '@/shared/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/dialog';
import type { SpecialtyCoverageItem, SpecialtyOption } from './types';

type BulkCopyDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  source: SpecialtyCoverageItem;
  allItems: SpecialtyCoverageItem[];
  specialtyOptions: SpecialtyOption[];
  onConfirm: (targetSpecialtyIds: string[]) => void;
};

function nameFor(id: string, options: SpecialtyOption[]): string {
  return options.find((opt) => opt.id === id)?.name ?? id;
}

export function BulkCopyDialog({
  open,
  onOpenChange,
  source,
  allItems,
  specialtyOptions,
  onConfirm,
}: BulkCopyDialogProps) {
  const [selected, setSelected] = useState<string[]>([]);
  const targets = allItems.filter((item) => item.specialtyId !== source.specialtyId);
  const sourceName = nameFor(source.specialtyId, specialtyOptions);

  const handleOpenChange = (next: boolean) => {
    if (next) setSelected([]);
    onOpenChange(next);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent aria-label="Copy coverage">
        <DialogHeader>
          <DialogTitle>Copy coverage</DialogTitle>
          <DialogDescription>
            Copy {sourceName}&apos;s coverage and delivery modes to other specialties.
            Existing configuration on selected specialties will be overwritten.
          </DialogDescription>
        </DialogHeader>

        <div className="max-h-64 space-y-2 overflow-y-auto">
          {targets.length === 0 ? (
            <p className="text-sm text-gray-500">No other specialties to copy to.</p>
          ) : (
            targets.map((item) => {
              const label = nameFor(item.specialtyId, specialtyOptions);
              const checked = selected.includes(item.specialtyId);
              return (
                <label
                  key={item.specialtyId}
                  className="flex items-center gap-2 rounded-md border px-3 py-2 text-sm"
                >
                  <Checkbox
                    checked={checked}
                    onCheckedChange={(value) => {
                      setSelected((current) =>
                        value === true
                          ? [...current, item.specialtyId]
                          : current.filter((id) => id !== item.specialtyId),
                      );
                    }}
                    aria-label={label}
                  />
                  <span>{label}</span>
                </label>
              );
            })
          )}
        </div>

        {selected.length > 0 ? (
          <p className="text-sm text-amber-800" role="status">
            Overwrite summary: {selected.length}{' '}
            {selected.length === 1 ? 'specialty' : 'specialties'} will receive{' '}
            {source.coverageScope === 'NATIONAL' ? 'National' : 'Regional'} coverage and{' '}
            {source.serviceRanges.length} delivery{' '}
            {source.serviceRanges.length === 1 ? 'mode' : 'modes'}.
          </p>
        ) : null}

        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            type="button"
            disabled={selected.length === 0}
            className="bg-bquest-green text-white hover:bg-bquest-green/90"
            onClick={() => {
              onConfirm(selected);
              onOpenChange(false);
            }}
          >
            Copy to selected
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
